import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages a footer popup dialog.
 * The popup appears after a delay and remembers if it has been shown.
 *
 * @extends DialogComponent
 */
export class FooterPopupComponent extends DialogComponent {
  /** @static @constant {string} The key prefix used to store shown state in localStorage */
  static #STORAGE_KEY_PREFIX = 'footer-popup-shown-';

  connectedCallback() {
    super.connectedCallback();

    // Check if we should always show in design mode
    const alwaysShowInDesignMode =
      this.dataset.alwaysShowDesignMode === 'true';
    const isDesignMode = document.documentElement.classList.contains(
      'shopify-design-mode'
    );

    // In design mode with setting enabled, always show (bypass localStorage check)
    if (isDesignMode && alwaysShowInDesignMode) {
      const delay = Number(this.dataset.delay) || 3;
      const delayMs = delay * 1000;
      setTimeout(() => {
        this.showDialog();
      }, delayMs);
      return;
    }

    // Check if this popup was already shown
    if (this.#hasBeenShown()) {
      return;
    }

    // Get delay from data attribute (in seconds)
    const delay = Number(this.dataset.delay) || 3;
    const delayMs = delay * 1000;

    // Show popup after delay
    setTimeout(() => {
      // Check again in case it was shown during the delay
      if (!this.#hasBeenShown()) {
        this.showDialog();
        // Mark as shown immediately when displaying
        this.#markAsShown();
      }
    }, delayMs);
  }

  /**
   * Marks this popup as shown in localStorage.
   * @private
   */
  #markAsShown() {
    const sectionId = this.dataset.sectionId;
    if (sectionId) {
      localStorage.setItem(
        `${FooterPopupComponent.#STORAGE_KEY_PREFIX}${sectionId}`,
        'true'
      );
    }
  }

  /**
   * Checks if this popup has already been shown.
   * @returns {boolean} True if the popup has been shown.
   * @private
   */
  #hasBeenShown() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      return false;
    }

    const shown = localStorage.getItem(
      `${FooterPopupComponent.#STORAGE_KEY_PREFIX}${sectionId}`
    );
    return shown === 'true';
  }
}

if (!customElements.get('footer-popup-component')) {
  customElements.define('footer-popup-component', FooterPopupComponent);
}
